import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/apiClient";
import { toast } from "sonner";
import { FooterLinks } from "../components/Footer";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError("Les mots de passe ne correspondent pas.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Mot de passe mis à jour");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-amber-600/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-amber-800/10 blur-3xl" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        <div className="flex items-center gap-2 justify-center mb-8">
          <img src={process.env.PUBLIC_URL + "/cehnura-mark.png"} alt="Cehnura" className="w-11 h-11 object-contain" />
          <span className="font-display font-extrabold text-2xl">Cehn<span className="gold-text">ura</span></span>
        </div>
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-8 relative grain" data-testid="reset-password-page">
          <div className="relative z-10">
            {done ? (
              <div className="text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                <h1 className="font-display text-2xl font-bold mb-2">C'est fait !</h1>
                <p className="text-sm text-zinc-400">Ton mot de passe a été mis à jour. Redirection vers la connexion…</p>
              </div>
            ) : !token ? (
              <div className="text-center">
                <h1 className="font-display text-2xl font-bold mb-2">Lien invalide</h1>
                <p className="text-sm text-zinc-400 mb-4">Ce lien de réinitialisation est incomplet ou expiré.</p>
                <Link to="/forgot-password" className="text-amber-400 hover:underline text-sm" data-testid="redo-forgot">Refaire une demande</Link>
              </div>
            ) : (
              <>
                <h1 className="font-display text-2xl font-bold mb-1">Nouveau mot de passe</h1>
                <p className="text-sm text-zinc-500 mb-6">Choisis un nouveau mot de passe pour ton compte.</p>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-zinc-400 mb-1.5">Nouveau mot de passe</label>
                    <div className="relative">
                      <input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" placeholder="••••••••" required
                        className="w-full h-12 px-4 pr-11 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-colors" />
                      <button type="button" onClick={() => setShow((s) => !s)} data-testid="reset-password-toggle" aria-label={show ? "Masquer" : "Afficher"}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-400">
                        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-400 mb-1.5">Confirmer</label>
                    <input type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" placeholder="••••••••" required
                      className="w-full h-12 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-colors" />
                  </div>
                  {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
                  <button type="submit" disabled={loading} data-testid="reset-submit"
                    className="w-full h-12 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Mettre à jour"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
        <FooterLinks />
      </motion.div>
    </div>
  );
}
